import {useEffect, useState} from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import {useQuery} from 'react-query';
import {useIntl} from 'react-intl';
import {Banner, getBannersTiDisplay} from './api';
import {categoryMeta, normalizeUrl} from './CurrentlyCompact';

// Clé de mémorisation selon la fréquence choisie pour le popup.
const storageKey = (id: number): string => `news_popup_${id}`;

const NewsPopupModal = (): JSX.Element | null => {
  const intl = useIntl();
  const {data: banners} = useQuery('banners', getBannersTiDisplay);
  const [show, setShow] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  // On ne gère qu'un seul popup à la fois (garanti côté backend).
  const popup: Banner | undefined = (banners ?? []).find((banner) => banner.type === 'popup');

  // sessionStorage pour 'session', localStorage pour 'once'.
  const storage = popup?.frequency === 'once' ? window.localStorage : window.sessionStorage;

  useEffect(() => {
    if (popup && !storage.getItem(storageKey(popup.id))) {
      setShow(true);
    }
  }, [popup, storage]);

  if (!popup) {
    return null;
  }

  const {label, accent} = categoryMeta(popup.category.name, intl);

  const handleHide = () => {
    storage.setItem(storageKey(popup.id), 'true');
    setShow(false);
  };

  return (
    <Modal show={show} onHide={handleHide} centered size="lg">
      <div
        style={{borderRadius: '10px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.12)'}}
      >
        {/* Header */}
        <div style={{background: '#1f223e', padding: '20px 24px 16px', position: 'relative'}}>
          <button
            onClick={handleHide}
            style={{
              position: 'absolute',
              top: '14px',
              right: '16px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '18px',
              lineHeight: 1,
              padding: '2px 6px',
            }}
          >
            ×
          </button>

          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '8px',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <i className="bi bi-info-circle" style={{fontSize: '17px', color: '#fff'}} />
            </div>
            <div>
              <span
                className="badge fw-semibold text-capitalize"
                style={{backgroundColor: accent, color: '#fff', letterSpacing: '0.5px'}}
              >
                {label}
              </span>
              <h6 style={{color: '#fff', margin: '4px 0 0', fontWeight: 700, fontSize: '15px'}}>
                {popup.title}
              </h6>
            </div>
          </div>
        </div>

        {/* Body */}
        <Modal.Body style={{padding: '20px 24px 16px', background: '#fff'}}>
          {popup.description && (
            <div
              style={{
                background: '#f8faff',
                borderLeft: `3px solid ${accent}`,
                borderRadius: '0 6px 6px 0',
                padding: '12px 16px',
                color: '#374151',
                fontSize: '13.5px',
                lineHeight: '1.65',
                whiteSpace: 'pre-line',
              }}
            >
              {popup.description}
            </div>
          )}
        </Modal.Body>

        {/* Footer */}
        <div
          style={{
            padding: '0 24px 20px',
            background: '#fff',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
          }}
        >
          {popup.news && (
            <a
              href={normalizeUrl(popup.news)}
              target="_blank"
              rel="noreferrer"
              onClick={handleHide}
              className="btn"
              style={{
                background: '#fff',
                border: `1px solid ${accent}`,
                color: accent,
                borderRadius: '7px',
                padding: '7px 20px',
                fontWeight: 600,
                fontSize: '13px',
              }}
            >
              {intl.formatMessage({id: 'COMMON.LEARN_MORE'})}
              <i className="bi bi-box-arrow-up-right ms-2" style={{color: accent}} />
            </a>
          )}
          <Button
            onClick={handleHide}
            onMouseEnter={() => setIsButtonHovered(true)}
            onMouseLeave={() => setIsButtonHovered(false)}
            style={{
              background: isButtonHovered ? '#0095e8' : '#1071b9',
              border: 'none',
              borderRadius: '7px',
              padding: '7px 20px',
              fontWeight: 600,
              fontSize: '13px',
            }}
          >
            {intl.formatMessage({id: 'COMMON.UNDERSTOOD'})}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default NewsPopupModal;
